// Chinese (Simplified) dictionary — same keys as en.ts.
import type { DictKey } from './dictionaries';

// The `Record<DictKey, string>` constraint forces every key in the
// English dictionary to be present here. tsc will fail the build if
// either file drifts out of sync.
export const zh: Record<DictKey, string> = {
  // ====== Navbar ======
  'nav.resources': '资源',
  'nav.controlRoom': '控制台',
  'nav.signIn': '登录',
  'nav.register': '注册',
  'nav.signOut': '退出',
  'nav.languageToggle': '中文',

  // ====== Footer ======
  'footer.libraryOnline': '资源库在线',
  'footer.builtForCreators': '为创作者打造',
  'footer.copyright': '© {year} CG 资源中心',

  // ====== Home ======
  'home.toolTagHoudini': 'Houdini',
  'home.toolTagLiveService': '在线服务',
  'home.toolTagHipRepair': 'HIP 修复',
  'home.toolTagFree': '免费',
  'home.toolRunServerSide': '服务端运行',
  'home.openTool': '打开工具',
  'home.view': '查看',
  'home.downloadsCount': '{count} 次下载',

  // ====== Login ======
  'login.signInSlash': '/ 登录',
  'login.welcomeBack': '欢迎回来',
  'login.subtitle': '登录后即可访问资源库',
  'login.usernameOrEmail': '用户名或邮箱',
  'login.usernamePlaceholder': 'zhangsan 或 you@example.com',
  'login.password': '密码',
  'login.passwordPlaceholder': '••••••••',
  'login.signInCta': '登录',
  'login.signingIn': '正在登录…',
  'login.or': '或',
  'login.invitePrompt': '有邀请码?',
  'login.createAccount': '创建账户',
  'login.invalidCredentials': '账号或密码错误',
  'login.loginFailed': '登录失败',
  'login.hidePassword': '隐藏密码',
  'login.showPassword': '显示密码',

  // ====== Register ======
  'register.accessDeniedSlash': '/ 拒绝访问',
  'register.missingInviteTitle': '缺少邀请码',
  'register.missingInviteBody':
    '本站采用邀请制注册。请联系现有成员获取邀请链接。',
  'register.backToSignIn': '返回登录',
  'register.createAccountSlash': '/ 创建账户',
  'register.joinTheLibrary': '加入资源库',
  'register.inviteBased': '邀请制注册',
  'register.invitationCode': '邀请码',
  'register.username': '用户名',
  'register.usernamePlaceholder': '例如:zhangsan',
  'register.emailOptional': '邮箱(选填)',
  'register.emailPlaceholder': 'you@example.com',
  'register.password': '密码',
  'register.passwordMin': '至少 6 个字符',
  'register.confirmPassword': '确认密码',
  'register.createCta': '创建账户',
  'register.creatingCta': '正在创建账户…',
  'register.haveAccount': '已有账户?',
  'register.signIn': '登录',
  'register.passwordMismatch': '两次输入的密码不一致',
  'register.registrationFailed': '注册失败',
  'register.unreachable': '无法连接到服务器',

  // ====== ResourceDetail ======
  'detail.backToLibrary': '返回资源库',
  'detail.breadcrumb': '/ 资源 / {category}',
  'detail.resourceNotFound': '未找到该资源',
  'detail.resourceNotFoundHint': '资源可能已被移除或从未存在',
  'detail.categoryUnreal': 'Unreal Engine',
  'detail.categoryResource': '{category} 资源',
  'detail.technicalHighlights': '/ 技术亮点',
  'detail.spec': '/ 文件规格',
  'spec.blenderVersion': 'Blender 版本',
  'spec.renderer': '渲染器',
  'spec.frameRange': '帧范围',
  'spec.fps': '帧率',
  'spec.resolution': '分辨率',
  'spec.lights': '灯光设置',
  'detail.videoDemo': '/ 演示视频',
  'detail.videoTimeReal': '{time}',
  'detail.videoTimePlaceholder': '{time} — 占位',
  'detail.demoBadge': '演示 · 16:9',
  'detail.videoPlaceholderCaption': '视频占位 · 即将上线',
  'detail.playVideo': '播放演示视频',
  'detail.screenshots': '/ 截图与效果',
  'detail.frames': '4 帧',
  'detail.screenshot': '截图',
  'detail.categorized': '/ 分类标签',
  'detail.tagSoftware': '软件',
  'detail.tagElement': '元素',
  'detail.tagTechnique': '技法',
  'detail.tags': '/ 标签',
  'detail.metaDownloads': '下载量',
  'detail.metaCategory': '分类',
  'detail.metaAdded': '添加于',
  'detail.getAsset': '下载此资源',

  // ====== Blend asset panel ======
  'detail.bl.blendBadge': 'Blender 资产',
  'detail.bl.fullDownload': '下载完整包',
  'detail.bl.assetCount': '{count} 个资产',
  'detail.bl.assetSingle': '1 个资产',
  'detail.bl.assetMaterials': '{count} 材质',
  'detail.bl.assetTextures': '{count} 贴图',
  'detail.bl.assetTriangles': '{count} 三角面',
  'detail.bl.assetVertices': '{count} 顶点',
  'detail.bl.assetHasArmature': '含绑定',
  'detail.bl.assetDownload': '下载此资产',
  'detail.bl.assetDownloading': '正在打包…',
  'detail.bl.assetFirstBuild': '首次打包约 5–10 秒,之后秒下',
  'detail.bl.allResolved': '贴图全部解析成功',
  'detail.bl.someMissing': '{count} 张贴图缺失',
  'detail.bl.noMissing': '无缺失',
  'detail.bl.thumbnailGenerating': '缩略图生成中…',
  'detail.bl.version': 'Blender 版本',
  'detail.bl.actionsNone': '无动画',

  // ====== ToolHipPathDoctor ======
  'tool.title': 'HIP 文件路径在线助手',
  'tool.signInPrompt': '登录后即可修复、替换、转换你的 .hip 文件中的路径。',
  'tool.signInCta': '登录',
  'tool.registerCta': '注册',
  'tool.liveService': '在线服务',
  'tool.houdiniVersion': 'Houdini 22.0.368',
  'tool.titleWithAccent': 'HIP 文件路径在线助手',
  'tool.lede':
    '拖入一个 .hip 文件,我们会逐条修复、替换、重新映射里面的所有路径 — 与 yaopc::FolderPathFixed HDA 同款逻辑,运行在后台的 Houdini 服务器上。',
  'tool.sectionFeatures': '选择一个功能',
  'tool.sectionSteps': '当前功能的操作步骤',
  'tool.sectionUpload': '上传并运行',
  'tool.featureSwitchSlash': '切换斜杠',
  'tool.featureSwitchSlashDesc': '把所有字符串参数里的反斜杠统一改成正斜杠。',
  'tool.featureReplace': '替换路径',
  'tool.featureReplaceDesc': '在每个参数里查找旧子串,替换成新子串。',
  'tool.featureFindMissing': '查找丢失',
  'tool.featureFindMissingDesc': '列出所有指向磁盘上不存在路径的 file / filecache 节点。',
  'tool.featureAbsRel': '绝对 / 相对互换',
  'tool.featureAbsRelDesc': '在绝对路径与基于 $VAR 的相对路径之间互相转换。',
  'tool.stepSwitchSlash': '无需额外选项。每个字符串参数里的反斜杠 {bs} 都会变成 {fs}。',
  'tool.stepReplace': '填入「旧子串」和「新子串」,然后点运行。',
  'tool.stepFindMissing': '输出的 .hip 保持原样 — 你拿到的是一份报告,列出所有指向不存在路径的 file / filecache 节点。',
  'tool.stepAbsRel': '选一个方向,选一个基准路径,然后点运行。',
  'tool.labelOld': '旧子串',
  'tool.placeholderOld': '例如:D:\\old_project\\assets',
  'tool.labelNew': '新子串',
  'tool.placeholderNew': '例如:G:\\projects\\shot010',
  'tool.labelDirection': '方向',
  'tool.labelBase': '基准',
  'tool.labelCustomBase': '自定义基准路径',
  'tool.placeholderCustomBase': '例如:$HIP/proj/ 或 /abs/path/',
  'tool.dirToAbs': '→ 绝对',
  'tool.dirToRel': '→ 相对',
  'tool.baseHip': '$HIP',
  'tool.baseJob': '$JOB',
  'tool.baseCustom': '自定义',
  'tool.uploadTitle': '上传你的 .hip',
  'tool.dropPrompt': '把 .hip 拖到这里,或点击浏览',
  'tool.dropHint': '.hip / .hipnc · 最大 100 MB',
  'tool.runCta': '运行',
  'tool.processingCta': '正在处理…',
  'tool.errorOnlyHip': '只接受 .hip 或 .hipnc 文件。',
  'tool.errorTooLarge': '文件过大,最大 100 MB。',
  'tool.errorDropFirst': '请先拖入一个 .hip 文件。',
  'tool.errorNeedOld': '替换功能需要填写「旧子串」。',
  'tool.errorNeedCustomBase': '基准选「自定义」时,必须填写自定义路径。',
  'tool.errorDefault': '运行失败。',
  'tool.resultDone': '完成',
  'tool.resultSavedAs': '已保存为 {filename},在你的下载文件夹里。',
  'tool.resultAuditHint':
    '审计日志已写入 .hip 同目录下:{mdPath}',
  'tool.resultRedownload': '重新下载',
  'tool.resultFallbackFindMissing':
    '查找丢失:扫描完成。\n(下载的 .hip 保持原样,未做任何修改)',
  'tool.resultFallbackOther': '{name}:共修改 {count} 个参数。',
  'tool.backToResources': '资源',

  // ====== ToolHipFormatBridge (added 2026-08-22) ======
  'home.toolTagFormatBridge': '格式桥接',
  'home.toolTagImportMesh': '3D 网格导入',
  'home.toolTagImportCad': 'CAD 导入',
  'bridge.title': 'HIP 格式桥接器',
  'bridge.signInPrompt':
    '登录后即可把 Houdini 不支持的 3D 文件转换成 .hip 场景。',
  'bridge.signInCta': '登录',
  'bridge.registerCta': '注册',
  'bridge.liveService': '在线服务',
  'bridge.houdiniVersion': 'Houdini 22 + FreeCAD 1.0',
  'bridge.lede':
    '拖入一个 Houdini 打不开的 3D 文件(.3ds / .step / .iges / .sat / .dae / .ply …)。服务端会把它转换成一个全新的 .hip,几何体嵌在 /obj/imported_geometry 里,直接打开就能用。',
  'bridge.sectionFormats': '支持的源格式',
  'bridge.sectionUpload': '上传并运行',
  'bridge.groupMesh': '3D 网格格式',
  'bridge.groupCad': 'CAD 格式',
  'bridge.group2d': '2D 图纸',
  'bridge.fmt3ds': '3D Studio',
  'bridge.fmt3mf': '3D 制造格式',
  'bridge.fmtdae': 'Collada',
  'bridge.fmtply': 'Stanford PLY',
  'bridge.fmtstl': 'STL',
  'bridge.fmtoff': 'Object File Format',
  'bridge.fmtstep': 'STEP (AP214)',
  'bridge.fmtstp': 'STEP',
  'bridge.fmtiges': 'IGES',
  'bridge.fmtigs': 'IGES',
  'bridge.fmtsat': 'ACIS SAT',
  'bridge.fmtsab': 'ACIS SAB',
  'bridge.fmtbrep': 'OpenCascade BREP',
  'bridge.fmtdxf': 'AutoCAD DXF',
  'bridge.uploadTitle': '上传你的 3D 文件',
  'bridge.dropPrompt': '把文件拖到这里,或点击浏览',
  'bridge.dropHint': '.3ds .3mf .step .iges .sat .dxf … 最大 200 MB',
  'bridge.runCta': '转换',
  'bridge.processingCta': '正在转换…',
  'bridge.errorOnly3d': '只接受支持的 3D 文件类型。',
  'bridge.errorTooLarge': '文件过大,最大 200 MB。',
  'bridge.errorDropFirst': '请先拖入一个 3D 文件。',
  'bridge.errorDefault': '运行失败。',
  'bridge.resultDone': '完成',
  'bridge.resultSavedAs': '已保存为 {filename},在你的下载文件夹里。',
  'bridge.resultAuditHint': '审计日志已写入 .hip 同目录下:{mdPath}',
  'bridge.resultStats': '已导入:{points} 个点,{prims} 个图元',
  'bridge.resultContainer': '容器:/obj/imported_geometry (File SOP → Null)',
  'bridge.backToResources': '资源',

  // ====== 3DGS Auto Trainer (added 2026-08-22) ======
  // 一键 COLMAP → Houdini ML 3DGS 流水线。web 工具上传图片 .zip,服务端
  // 用 hython 跑 gsplats_auto_trainer.py,返回 .hip + 审计 MD。训练本身
  // 需在 Houdini 本地触发 (cook 时间太长,不适合 HTTP 请求)。
  'home.toolTagGsplats': '3DGS 自动',
  'gsplats.title': '3DGS 自动训练器',
  'gsplats.signInPrompt': '登录后即可把一组图片转成 Houdini 3D 高斯泼溅场景。',
  'gsplats.signInCta': '登录',
  'gsplats.registerCta': '注册',
  'gsplats.liveService': '在线服务',
  'gsplats.houdiniVersion': 'Houdini 22 + COLMAP',
  'gsplats.lede':
    '拖入一个图片 .zip。服务端会跑 COLMAP、构建带 ml_train_gsplats TOP 网络和 /obj/geo 预览链的 Houdini 场景,返回一个开箱即用、可直接 Cook 的 .hip 文件。在 Houdini 22 里打开它,就能跑真正的训练。',
  'gsplats.sectionPipeline': '流水线',
  'gsplats.sectionUpload': '上传并运行',
  'gsplats.pipelineColmap': 'COLMAP(feature_extractor → matcher → mapper → model_converter,OPENCV 相机模型)',
  'gsplats.pipelineTop': '/tasks/ml_train_gsplats — VRAM 缓存,最大批 6,禁用测试导出',
  'gsplats.pipelineSop': '/obj/geo — file → bakegsplats → transform → group(bbox) → blast → OUT_SPLAT',
  'gsplats.labelProjectName': '工程名',
  'gsplats.placeholderProjectName': '例如: white_lion',
  'gsplats.helpProjectName': '用于数据集目录名和 .hip 文件名,字母/数字/下划线。',
  'gsplats.labelDownscale': '图片降采样',
  'gsplats.downscaleFull': '原图 (1×)',
  'gsplats.downscaleHalf': '半身 (½)',
  'gsplats.downscaleQuarter': '四分之一 (¼)',
  'gsplats.labelMaxBatch': '最大批大小',
  'gsplats.helpMaxBatch': '显存小时调低;6 适配单张 24 GB 显卡。',
  'gsplats.labelBbox': '预览包围盒 (半边长)',
  'gsplats.helpBbox': 'Houdini 单位,完整包围盒 = 此值 ×2 每条轴。',
  'gsplats.labelDryRun': 'Dry-run (跳过 COLMAP + Cook)',
  'gsplats.helpDryRun': '当 COLMAP 未装、只想验证 .hip 结构时勾选。会建好数据集目录 + .hip,但 sparse/0/ 是占位文件。',
  'gsplats.uploadTitle': '上传图片 .zip',
  'gsplats.dropPrompt': '把图片 .zip 拖到这里,或点击浏览',
  'gsplats.dropHint': '.zip · 最大 500 MB',
  'gsplats.runCta': '生成 .hip',
  'gsplats.processingCta': '正在生成…',
  'gsplats.errorOnlyZip': '只接受 .zip 文件,请先把图片文件夹压缩成 .zip。',
  'gsplats.errorTooLarge': '文件过大,最大 500 MB。',
  'gsplats.errorDropFirst': '请先拖入一个图片 .zip。',
  'gsplats.errorNeedProjectName': '工程名必填,字母/数字/下划线。',
  'gsplats.errorDefault': '运行失败。',
  'gsplats.resultDone': '完成',
  'gsplats.resultSavedAs': '已保存为 {filename},在你的下载文件夹里。',
  'gsplats.resultAuditHint': '审计日志已写入 .hip 同目录下:{mdPath}',
  'gsplats.resultRedownload': '重新下载',
  'gsplats.resultDryRunNote': 'Dry-run 模式:sparse/0/ 是占位文件,取消勾选 Dry-run 重新跑即可生成真实 COLMAP 输出。',
  'gsplats.backToResources': '资源',

  // ====== Taxonomy: filters & badges (Home / Detail) ======
  'home.filterAll': '全部',
  'home.filterCategory': '软件',
  'home.filterType': '类型',
  'home.filterPrice': '价格',
  'home.filterSort': '排序',
  'home.sortNew': '最新',
  'home.sortDownloads': '最多下载',
  'home.filterClear': '清除',
  'home.filterEmpty': '没有符合条件的资源',
  'home.badgeFree': '免费',
  'home.badgePaid': '付费',

  // ====== Taxonomy: resource types ======
  'resourceType.plugin': '插件 / 脚本',
  'resourceType.preset': '预设 / 模板',
  'resourceType.material': '材质 / 贴图',
  'resourceType.model': '模型 / 资产',
  'resourceType.project': '工程文件',
  'resourceType.tutorial': '教程 / 文档',
  'resourceType.aiworkflow': 'AI 工作流包',
  'resourceType.audio': '音效 / 音乐',

  // ====== Taxonomy: licenses ======
  'license.cc0': 'CC0',
  'license.mit': 'MIT',
  'license.gpl': 'GPL',
  'license.commercial': '商业许可',

  // ====== Taxonomy: languages ======
  'language.zh': '中文',
  'language.en': '英文',
  'language.localized': '汉化',
};
